import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Check, X, CreditCard, Clock, Ban, MapPin, Percent, Sparkles, ShieldCheck } from "lucide-react";

const INPUT = "w-full bg-[#09090b] border border-[#3f3f46] text-[#fafafa] placeholder-[#71717a] focus:border-[#c8aa6e] outline-none px-3 py-2 text-sm";
const SMALL = "text-xs border px-2 py-1 whitespace-nowrap transition-colors disabled:opacity-40";

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" });
}

const SOURCE_BADGE = {
  admin: { label: "admin", cls: "border-[#c8aa6e] text-[#c8aa6e]", Icon: ShieldCheck },
  stripe: { label: "Stripe", cls: "border-[#6366f1]/60 text-[#818cf8]", Icon: CreditCard },
  manual: { label: "manual", cls: "border-[#10b981]/60 text-[#10b981]", Icon: Check },
  trial: { label: "trial", cls: "border-[#f59e0b]/60 text-[#f59e0b]", Icon: Clock },
  none: { label: "sem plano", cls: "border-[#3f3f46] text-[#71717a]", Icon: X },
};

function GrantDialog({ open, onClose, targetUser, durations, plans, cityOptions, onDone }) {
  const [planId, setPlanId] = useState("city1");
  const [durationKey, setDurationKey] = useState("monthly");
  const [cities, setCities] = useState([]);
  const [busy, setBusy] = useState(false);
  const [prevUser, setPrevUser] = useState(null);

  // reset form when a different user is opened
  const uid = targetUser?.id || null;
  if (uid !== prevUser) {
    setPrevUser(uid);
    setPlanId(targetUser?.plan && plans.some((p) => p.id === targetUser.plan) ? targetUser.plan : "city1");
    setDurationKey("monthly");
    setCities(Array.isArray(targetUser?.allowed_cities) ? targetUser.allowed_cities : []);
  }

  const plan = plans.find((p) => p.id === planId);
  const duration = durations.find((d) => d.key === durationKey);
  const price = duration?.prices?.[planId];
  const needCities = plan && plan.cities_limit != null;

  const toggleCity = (c) => setCities((prev) => {
    if (prev.includes(c)) return prev.filter((x) => x !== c);
    if (prev.length >= plan.cities_limit) return prev;
    return [...prev, c];
  });

  const submit = async () => {
    if (needCities && cities.length < 1) { toast.error("Seleciona pelo menos uma cidade"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/subscriptions/grant", {
        user_id: targetUser.id, plan_id: planId, duration_key: durationKey,
        cities: needCities ? cities.slice(0, plan.cities_limit) : [],
      });
      toast.success(`Subscrição ativada · ${data.charged}€ · expira ${fmtDate(data.expires_at)}`);
      onDone();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao ativar subscrição");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#18181b] border border-[#3f3f46] rounded-none text-[#fafafa] max-w-lg" data-testid="grant-dialog">
        <DialogHeader><DialogTitle className="font-head font-normal text-lg">Ativar subscrição</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-[#a1a1aa]">Cliente: <span className="text-[#fafafa]">{targetUser?.email}</span></p>

          <div>
            <label className="label-tiny block mb-1">Plano</label>
            <div className="grid grid-cols-3 gap-2" data-testid="grant-plans">
              {plans.map((p) => (
                <button key={p.id} onClick={() => setPlanId(p.id)} data-testid={`grant-plan-${p.id}`}
                  className={`px-3 py-2 text-sm border text-left ${planId === p.id ? "border-[#c8aa6e] bg-[#c8aa6e]/10 text-[#c8aa6e]" : "border-[#3f3f46] text-[#a1a1aa] hover:border-[#c8aa6e]"}`}>
                  {p.name}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="label-tiny block mb-1">Duração</label>
            <div className="grid grid-cols-4 gap-2" data-testid="grant-durations">
              {durations.map((d) => (
                <button key={d.key} onClick={() => setDurationKey(d.key)} data-testid={`grant-duration-${d.key}`}
                  className={`px-2 py-2 text-xs border text-center ${durationKey === d.key ? "border-[#c8aa6e] bg-[#c8aa6e]/10 text-[#c8aa6e]" : "border-[#3f3f46] text-[#a1a1aa] hover:border-[#c8aa6e]"}`}>
                  <div>{d.label}</div>
                  {d.discount_percent > 0 && <div className="text-[10px] text-[#c8aa6e] mt-0.5">-{d.discount_percent}%</div>}
                </button>
              ))}
            </div>
          </div>

          {needCities && (
            <div>
              <label className="label-tiny block mb-1">Cidades ({cities.length}/{plan.cities_limit})</label>
              <div className="grid grid-cols-3 gap-2 max-h-40 overflow-y-auto" data-testid="grant-cities">
                {cityOptions.map((c) => {
                  const on = cities.includes(c);
                  const disabled = !on && cities.length >= plan.cities_limit;
                  return (
                    <button key={c} onClick={() => toggleCity(c)} disabled={disabled} data-testid={`grant-city-${c}`}
                      className={`px-2 py-1.5 text-xs border text-left ${on ? "border-[#c8aa6e] bg-[#c8aa6e]/10 text-[#c8aa6e]" : disabled ? "border-[#27272a] text-[#52525b]" : "border-[#3f3f46] text-[#a1a1aa] hover:border-[#c8aa6e]"}`}>
                      <span className="inline-flex items-center gap-1">{on && <Check className="w-3 h-3" />}{c}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {price && (
            <div className="border border-[#c8aa6e]/30 bg-[#c8aa6e]/5 px-4 py-3 flex items-end justify-between" data-testid="grant-price">
              <div>
                <div className="label-tiny text-[#c8aa6e] mb-1">Total do período</div>
                <div className="font-head text-3xl font-light text-[#c8aa6e]">{price.total}€</div>
                <div className="text-xs text-[#71717a] mt-1">≈ {price.per_month}€/mês{price.saving > 0 ? ` · poupa ${price.saving}€` : ""}</div>
              </div>
              <div className="text-right text-xs text-[#71717a]">{duration?.label}<br />{duration?.days} dias de acesso</div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-[#3f3f46] text-[#a1a1aa] hover:text-[#fafafa]">Cancelar</button>
            <button onClick={submit} disabled={busy} data-testid="grant-submit"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-[#c8aa6e] text-black hover:bg-[#d4b982] disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 spin" /> : <Check className="w-4 h-4" />} Ativar subscrição
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DiscountEditor({ durations, plans, onSaved }) {
  const [disc, setDisc] = useState({});
  const [busy, setBusy] = useState(false);
  const [prev, setPrev] = useState(null);
  const key = durations.map((d) => `${d.key}:${d.discount_percent}`).join("|");
  if (key !== prev) {
    setPrev(key);
    setDisc(Object.fromEntries(durations.map((d) => [d.key, d.discount_percent])));
  }
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/billing-config", { discounts: disc });
      toast.success("Descontos por duração atualizados");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar");
    } finally { setBusy(false); }
  };
  return (
    <div className="bg-[#18181b] border border-[#27272a] p-5" data-testid="discount-editor">
      <div className="flex items-center gap-2 mb-1">
        <Percent className="w-4 h-4 text-[#c8aa6e]" />
        <h3 className="font-head text-lg text-[#fafafa]">Desconto por duração</h3>
      </div>
      <p className="text-xs text-[#71717a] mb-4">Percentagem de desconto aplicada ao preço mensal de cada plano. Quem subscreve por mais tempo paga menos por mês.</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {durations.map((d) => (
          <div key={d.key}>
            <label className="label-tiny block mb-1">{d.label}</label>
            <div className="flex items-center gap-1">
              <input type="number" min={0} max={90} step="1" value={disc[d.key] ?? 0} data-testid={`discount-${d.key}`}
                onChange={(e) => setDisc((s) => ({ ...s, [d.key]: e.target.value === "" ? 0 : Number(e.target.value) }))}
                disabled={d.key === "monthly"}
                className={`${INPUT} ${d.key === "monthly" ? "opacity-50" : ""}`} />
              <span className="text-sm text-[#71717a]">%</span>
            </div>
          </div>
        ))}
      </div>
      {/* Live price preview */}
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm" data-testid="price-preview">
          <thead>
            <tr className="label-tiny">
              <th className="px-3 py-2">Plano</th>
              {durations.map((d) => <th key={d.key} className="px-3 py-2">{d.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {plans.map((p) => (
              <tr key={p.id} className="border-t border-[#27272a]">
                <td className="px-3 py-2 text-[#fafafa]">{p.name}</td>
                {durations.map((d) => {
                  const pr = d.prices?.[p.id];
                  return (
                    <td key={d.key} className="px-3 py-2 text-[#a1a1aa]">
                      <span className="text-[#c8aa6e]">{pr?.total}€</span>
                      <span className="text-[10px] text-[#71717a] block">{pr?.per_month}€/mês</span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-end mt-4">
        <button onClick={save} disabled={busy} data-testid="save-discounts"
          className="inline-flex items-center gap-1.5 bg-[#c8aa6e] text-black hover:bg-[#d4b982] px-4 py-2 text-sm font-medium disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 spin" /> : <Check className="w-4 h-4" />} Guardar descontos
        </button>
      </div>
    </div>
  );
}

const KPI = ({ label, value, gold, testid }) => (
  <div data-testid={testid} className="bg-[#18181b] border border-[#27272a] p-5">
    <div className="label-tiny mb-3">{label}</div>
    <div className={`font-head text-3xl font-light ${gold ? "text-[#c8aa6e]" : "text-[#fafafa]"}`}>{value}</div>
  </div>
);

export default function SubscriptionsView() {
  const [config, setConfig] = useState({ durations: [], plans: [], discounts: {} });
  const [subs, setSubs] = useState({ items: [], summary: { active: 0, trial: 0, expired: 0 } });
  const [cityOptions, setCityOptions] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [grantUser, setGrantUser] = useState(null);

  const loadConfig = useCallback(() => {
    api.get("/admin/billing-config").then(({ data }) => setConfig(data));
  }, []);
  const loadSubs = useCallback(() => {
    api.get("/admin/subscriptions", { params: { search, status: statusFilter } }).then(({ data }) => setSubs(data));
  }, [search, statusFilter]);

  useEffect(() => { loadConfig(); api.get("/billing/cities-options").then(({ data }) => setCityOptions(data)).catch(() => {}); }, [loadConfig]);
  useEffect(() => { const t = setTimeout(loadSubs, 300); return () => clearTimeout(t); }, [loadSubs]);

  const trial = async (u, days) => {
    try {
      await api.post("/admin/subscriptions/trial", { user_id: u.id, days });
      toast.success(`Trial de ${days} dias concedido a ${u.email}`);
      loadSubs();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const revoke = async (u) => {
    if (!window.confirm(`Revogar o acesso de ${u.email}?`)) return;
    try {
      await api.post("/admin/subscriptions/revoke", { user_id: u.id });
      toast.success(`Acesso revogado a ${u.email}`);
      loadSubs();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const FILTERS = [
    { k: "", label: "Todos" },
    { k: "active", label: "Ativos" },
    { k: "trial", label: "Em teste" },
    { k: "expired", label: "Expirados" },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <KPI label="Subscrições ativas" value={subs.summary.active} gold testid="sub-kpi-active" />
        <KPI label="Em período de teste" value={subs.summary.trial} testid="sub-kpi-trial" />
        <KPI label="Expirados" value={subs.summary.expired} testid="sub-kpi-expired" />
      </div>

      <DiscountEditor durations={config.durations} plans={config.plans} onSaved={loadConfig} />

      <div className="bg-[#18181b] border border-[#27272a]" data-testid="subscriptions-table">
        <div className="p-4 border-b border-[#27272a] flex flex-wrap items-center gap-3">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Pesquisar email…" data-testid="subs-search" className="w-full max-w-xs bg-[#09090b] border border-[#3f3f46] text-[#fafafa] placeholder-[#71717a] focus:border-[#c8aa6e] outline-none px-3 py-2 text-sm" />
          <div className="flex border border-[#27272a]">
            {FILTERS.map((f) => (
              <button key={f.k} onClick={() => setStatusFilter(f.k)} data-testid={`subs-filter-${f.k || "all"}`}
                className={`px-3 py-2 text-xs whitespace-nowrap ${statusFilter === f.k ? "bg-[#c8aa6e] text-black" : "text-[#a1a1aa] hover:text-[#fafafa]"}`}>{f.label}</button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="label-tiny">
                <th className="px-4 py-3">Email</th><th className="px-4 py-3">Origem</th>
                <th className="px-4 py-3">Plano</th><th className="px-4 py-3">Cidades</th>
                <th className="px-4 py-3">Acesso</th><th className="px-4 py-3">Expira</th>
                <th className="px-4 py-3">Ações</th>
              </tr>
            </thead>
            <tbody>
              {subs.items.map((u, i) => {
                const b = SOURCE_BADGE[u.subscription_source] || SOURCE_BADGE.none;
                return (
                  <tr key={u.id} data-testid={`sub-row-${i}`} className="border-t border-[#27272a] hover:bg-[#27272a]/30">
                    <td className="px-4 py-2 text-[#fafafa]">{u.email}</td>
                    <td className="px-4 py-2">
                      <span className={`text-xs px-2 py-0.5 border inline-flex items-center gap-1 ${b.cls}`}><b.Icon className="w-3 h-3" />{b.label}</span>
                    </td>
                    <td className="px-4 py-2 text-[#a1a1aa]">{u.plan || "—"}</td>
                    <td className="px-4 py-2 text-[#a1a1aa] text-xs">
                      {u.allowed_cities == null ? <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3 text-[#c8aa6e]" />todas</span> : (u.allowed_cities.join(", ") || "—")}
                    </td>
                    <td className="px-4 py-2">
                      {u.is_active ? <span className="text-[#10b981] inline-flex items-center gap-1 text-xs"><Check className="w-3 h-3" />ativo</span>
                        : <span className="text-[#ef4444] inline-flex items-center gap-1 text-xs"><X className="w-3 h-3" />inativo</span>}
                    </td>
                    <td className="px-4 py-2 text-[#a1a1aa] whitespace-nowrap">{fmtDate(u.access_expires_at)}</td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <button onClick={() => setGrantUser(u)} data-testid={`sub-grant-${i}`}
                          className={`${SMALL} border-[#c8aa6e] text-[#c8aa6e] hover:bg-[#c8aa6e]/10 inline-flex items-center gap-1`}><CreditCard className="w-3 h-3" />Ativar</button>
                        <button onClick={() => trial(u, 7)} data-testid={`sub-trial-${i}`}
                          className={`${SMALL} border-[#3f3f46] text-[#a1a1aa] hover:border-[#f59e0b] inline-flex items-center gap-1`}><Sparkles className="w-3 h-3" />Trial 7d</button>
                        <button onClick={() => revoke(u)} data-testid={`sub-revoke-${i}`}
                          className={`${SMALL} border-[#3f3f46] text-[#a1a1aa] hover:border-[#ef4444] hover:text-[#ef4444] inline-flex items-center gap-1`}><Ban className="w-3 h-3" />Revogar</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {subs.items.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-[#71717a] text-sm">Sem subscrições neste filtro.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <GrantDialog open={!!grantUser} onClose={() => setGrantUser(null)} targetUser={grantUser}
        durations={config.durations} plans={config.plans} cityOptions={cityOptions} onDone={loadSubs} />
    </div>
  );
}
